// --- Extraction DTOs (from Gemini) ---

export interface ClientInfo {
  company_name: string | null;
  contact_person: string | null;
  contact_number: string | null;
  email: string | null;
  designation: string | null;
  company_address: string | null;
}

export interface ProjectInfo {
  project_name: string | null;
  project_location: string | null;
  delivery_address: string | null;
  delivery_schedule: string | null;
  reference_number: string | null;
  source_of_referral: string | null;
}

// --- Matching DTOs (from backend) ---

export interface PossibleMatch {
  itemId: string;
  lengthM: number;
  salesDescription: string;
  weightPerPcKg: number;
}

export interface MatchedLineItem {
  lineNumber: number;
  rawDescription: string | null;
  structuralElement: string | null;
  diameterMm: number | null;
  lengthM: number | null;
  gradeCode: number | null;
  isWeldable: boolean;
  quantity: number | null;
  unit: string;
  notes: string | null;
  matchStatus: string;
  matchedItemId: string | null;
  matchedSalesDescription: string | null;
  weightPerPcKg: number | null;
  totalWeightKg: number | null;
  basePricePerPc: number | null;
  averagePricePerPc: number | null;
  possibleMatches: PossibleMatch[];
  /** CSC-291: pre-promotion grade if `GradePromotionRules` fired at extraction time. */
  promotedFromGrade: number | null;
  /** 14ykpqtw3hw QUO-R-004: the customer's quantity/unit when the quotation rule rounded or converted the
   *  line (or could not convert a kg order). Null on the Sales Order pipeline, which never runs the rule. */
  sourceQuantity?: number | null;
  sourceUnit?: string | null;
}

export interface MatchedQuotationGroup {
  quotationLabel: string | null;
  lineItems: MatchedLineItem[];
}

export interface MatchingSummary {
  totalItems: number;
  matchedCount: number;
  needsClarificationCount: number;
  noMatchCount: number;
  totalWeightKg: number;
  gradesFound: string[];
}

export interface MatchingResult {
  clientInfo: ClientInfo | null;
  projectInfo: ProjectInfo | null;
  specialInstructions: string[];
  quotations: MatchedQuotationGroup[];
  summary: MatchingSummary;
  /** True when no items in the source had quantities and we defaulted every item to 1 pc (CSC-265). */
  quantityDefaultedToOne: boolean;
  /** How many items were stamped with quantity=1 in Scenario A. */
  itemsDefaultedToOneCount: number;
  /** How many items were dropped in Scenario B (mixed RFQ — some specs had qty, some didn't). */
  itemsDroppedForMissingQuantity: number;
  /** 14ykpqtw3hw QUO-R-003a: kg lines dropped because the same bar is listed in pieces (quotation routes only). */
  kilogramRepeatsDropped?: number;
  /** 14ykpqtw3hw QUO-R-003c: kg lines dropped as the total of the other kg lines (quotation routes only). */
  kilogramTotalsDropped?: number;
}

// --- Master Data ---

export interface MasterDataStats {
  itemCount: number;
  gradeCount: number;
  customerCount: number;
  lastSyncedAt: string | null;
  customersLastSyncedAt: string | null;
}

export interface MasterDataSyncResult {
  itemsUpserted: number;
  gradesUpserted: number;
  customersUpserted: number;
  syncedAt: string;
}

// --- Terms ---

export interface TermOption {
  id: string;
  name: string;
}

// --- Base Markup (per-grade markup percentages from NetSuite) ---

export interface BaseMarkup {
  effectiveDate: string;
  g40: number;
  g60: number;
  g75: number;
  g80: number;
}

// --- Project Categories ---

export interface ProjectCategoryOption {
  id: string;
  name: string;
}

// --- Sales Rep / Sales Coordinator (both NetSuite employee records) ---

export interface SalesRepOption {
  id: string;
  name: string;
}

export interface SalesCoordinatorOption {
  id: string;
  name: string;
}

// --- Customer combobox ---

export interface CustomerOption {
  internalId: string;
  entityId: string;
  companyName: string;
  email?: string | null;
  phone?: string | null;
  entityStatusName?: string | null;
  parentCompanyId?: string | null;
  parentCompanyName?: string | null;
  termsId?: string | null;
  termsName?: string | null;
  creditLimit?: number | null;
  /** Tax rate as a decimal (e.g. 0.12 for 12%). Null when the customer has no taxitem linked. */
  taxRate?: number | null;
  /** Default sales rep (NetSuite employee internal ID) for this customer. */
  salesRepId?: string | null;
  /** Default sales coordinator (NetSuite employee internal ID) for this customer. */
  salesCoordId?: string | null;
}

export type MatchStatus =
  | 'MATCHED'
  | 'NEEDS_CLARIFICATION'
  | 'NO_MATCH'
  | 'SUMMARY_ONLY'
  | 'EXTERNAL_ATTACHMENT';

// --- Re-match (single line item) ---

export interface RematchLineItemRequest {
  lineNumber?: number;
  rawDescription?: string | null;
  structuralElement?: string | null;
  diameterMm: number;
  lengthM: number;
  gradeCode: number;
  isWeldable: boolean;
  quantity?: number;
  unit?: string;
  notes?: string | null;
}

// --- Master-data admin force-sync discriminator ---
// Mirrors backend `MasterDataRecordTypes` constants. Wire strings — must match the
// NetSuite record-type ids that the admin endpoint dispatches on.
export const MASTER_DATA_RECORD_TYPES = {
  TruckingRates: 'customrecord_truckingrates',
  Brackets: 'customrecord_brk',
  BaseMarkupValues: 'customrecord_basemarking_values',
  Customers: 'customer',
} as const;

export type MasterDataRecordType =
  (typeof MASTER_DATA_RECORD_TYPES)[keyof typeof MASTER_DATA_RECORD_TYPES];
